import { useAppStore } from '@/store';
import type { SeverityLevel, AllergenSeverity } from '@/types/profile';
import { Tooltip } from './Tooltip';
import styles from './SeveritySelector.module.css';

const SEVERITY_DESCRIPTIONS: Record<SeverityLevel, string> = {
  must_avoid: 'Any amount is unsafe — strict avoidance required.',
  may_tolerate: 'Small or trace amounts may be acceptable — use clinical judgement.',
  cross_contact_alert: 'Flag for cross-contact risk only — not a direct ingredient concern.',
};

export function SeveritySelector() {
  const profiles = useAppStore((s) => s.profiles);
  const activeProfileId = useAppStore((s) => s.activeProfileId);
  const updateProfile = useAppStore((s) => s.updateProfile);

  const activeProfile = profiles.find((p) => p.id === activeProfileId);
  if (!activeProfile) {
    return (
      <div className={styles.severitySelector}>
        <div className={styles.emptyState}>Select a profile to set severity levels.</div>
      </div>
    );
  }

  const severities: AllergenSeverity[] = activeProfile.allergenSeverities ?? [];

  const getSeverity = (allergenId: string): SeverityLevel => {
    const entry = severities.find((s) => s.allergenId === allergenId);
    return entry?.severity ?? 'must_avoid';
  };

  const handleChange = (allergenId: string, severity: SeverityLevel) => {
    const existing = severities.find((s) => s.allergenId === allergenId);
    const updated = existing
      ? severities.map((s) => (s.allergenId === allergenId ? { ...s, severity } : s))
      : [...severities, { allergenId, severity }];
    updateProfile({ allergenSeverities: updated } as never);
  };

  return (
    <div className={styles.severitySelector}>
      {activeProfile.selectedAllergens.map((allergenId) => (
        <div key={allergenId} className={styles.allergenRow}>
          <span className={styles.allergenName}>{allergenId}</span>
          <div className={styles.selectWrapper}>
            <select
              className={styles.select}
              value={getSeverity(allergenId)}
              onChange={(e) => handleChange(allergenId, e.target.value as SeverityLevel)}
              title={SEVERITY_DESCRIPTIONS[getSeverity(allergenId)]}
            >
              <option value="must_avoid">Must Avoid</option>
              <option value="may_tolerate">May Tolerate</option>
              <option value="cross_contact_alert">Cross-Contact Alert</option>
            </select>
            <Tooltip content={SEVERITY_DESCRIPTIONS[getSeverity(allergenId)]} />
          </div>
        </div>
      ))}
    </div>
  );
}
